import React, { createContext, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { getAssetCategoriesService, getAssetsByCustomer } from "../../services/commonService";
import { LARGE_SELECT_LIMIT } from "./utils/select-constants";

const AssetSelectContext = createContext(null);

export const useAssetApiSelectContext = () => {
  return useContext(AssetSelectContext);
};

export const AssetApiSelectProvider = ({ children }) => {
  const cacheRef = useRef({});
  const pendingRequestsRef = useRef({});

  const getCacheKey = (customerId, filterCategories, variables) => {
    return JSON.stringify({
      customerId,
      filterCategories: filterCategories?.sort() || [],
      variables: variables || {},
    });
  };

  const fetchAssets = (customerId, filterCategories, variables, callback) => {
    const cacheKey = getCacheKey(customerId, filterCategories, variables);

    if (cacheRef.current[cacheKey]) {
      callback(cacheRef.current[cacheKey]);
      return;
    }

    if (pendingRequestsRef.current[cacheKey]) {
      pendingRequestsRef.current[cacheKey].push(callback);
      return;
    }

    pendingRequestsRef.current[cacheKey] = [callback];

    const assetsCallback = data => {
      cacheRef.current[cacheKey] = data;

      pendingRequestsRef.current[cacheKey].forEach(cb => cb(data));
      delete pendingRequestsRef.current[cacheKey];
    };

    const assetCategoriesCallback = data => {
      const categoryIds = data
        .filter(category => filterCategories.includes(category.codename))
        .map(category => category._id);

      getAssetsByCustomer(
        customerId,
        { ...variables, category_ids: categoryIds, limit: LARGE_SELECT_LIMIT },
        assetsCallback,
      );
    };

    if (filterCategories?.length) {
      getAssetCategoriesService(assetCategoriesCallback);
    } else {
      getAssetsByCustomer(customerId, { ...variables, limit: LARGE_SELECT_LIMIT }, assetsCallback);
    }
  };

  const clearCache = () => {
    cacheRef.current = {};
  };

  return <AssetSelectContext.Provider value={{ fetchAssets, clearCache }}>{children}</AssetSelectContext.Provider>;
};

AssetApiSelectProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
